import { post, get } from '@/utils/request'
import { withTwoFactor, type TwoFactorOptions } from '@/utils/twoFactor'
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  LoginRequest,
  LoginResult,
  LoginResponse,
  LoginTwoFactorVerifyRequest,
  LoginTwoFactorSetupRequest,
  LoginTwoFactorSetupResponse,
  LoginTwoFactorEnableRequest,
  MeResponse,
  TwoFactorSetupResponse,
  TwoFactorEnableRequest,
  TwoFactorEnableResponse,
} from '@/types/auth'

/**
 * 管理员登录第一步：邮箱 + 密码。
 * 当全局开关 `admin.security.two_factor.login_enforce` 开启时返回 2FA 挑战
 * （`LoginChallengeResponse`），否则直接签发 token（`LoginResponse`）。见后端 #1733。
 * @param config 透传 axios 配置（如 skipErrorToast，便于登录页自行展示 2FA 相关错误）。
 */
export function login(
  data: LoginRequest,
  config?: Parameters<typeof post>[2]
): Promise<LoginResult> {
  return post<LoginResult>('/admin/v1/auth/login', data, config)
}

/**
 * 管理员登录第二步：带第一步返回的 `challenge_token` + 6 位 TOTP 动态码完成校验，
 * 通过后签发正式 token。见后端 #1733。
 * @param config 透传 axios 配置（如 skipErrorToast，便于登录页自行展示挑战/动态码错误）。
 */
export function verifyLoginTwoFactor(
  data: LoginTwoFactorVerifyRequest,
  config?: Parameters<typeof post>[2]
): Promise<LoginResponse> {
  return post<LoginResponse>('/admin/v1/auth/login/2fa-verify', data, config)
}

/**
 * 挑战态自助绑定第一步：用第一步返回的 `challenge_token` 拉取待绑定 secret 与 otpauth URI。
 * 供未绑定 2FA 的账号在登录页直接扫码绑定（无需先登录）。绑定前重复调用返回同一 secret。
 * 见后端 #1811。
 * @param config 透传 axios 配置（如 skipErrorToast，便于登录页自行处理挑战失效 / 开关关闭错误）。
 */
export function setupLoginTwoFactor(
  data: LoginTwoFactorSetupRequest,
  config?: Parameters<typeof post>[2]
): Promise<LoginTwoFactorSetupResponse> {
  return post<LoginTwoFactorSetupResponse>('/admin/v1/auth/login/2fa-setup', data, config)
}

/**
 * 挑战态自助绑定第二步：带 `challenge_token` + 6 位 TOTP 动态码启用 2FA 并直接完成登录，
 * 返回与 `verifyLoginTwoFactor` 同构的 `LoginResponse`（含 token）。见后端 #1811。
 * @param config 透传 axios 配置（如 skipErrorToast，便于登录页自行展示动态码 / 挑战错误）。
 */
export function enableLoginTwoFactor(
  data: LoginTwoFactorEnableRequest,
  config?: Parameters<typeof post>[2]
): Promise<LoginResponse> {
  return post<LoginResponse>('/admin/v1/auth/login/2fa-enable', data, config)
}

export function logout(): Promise<void> {
  return post<void>('/admin/v1/auth/logout')
}

export function getMe(): Promise<MeResponse> {
  return get<MeResponse>('/admin/v1/auth/me')
}

/** 生成或返回当前管理员的 TOTP secret 与 otpauth URI（绑定前）。 */
export function setupTwoFactor(): Promise<TwoFactorSetupResponse> {
  return post<TwoFactorSetupResponse>('/admin/v1/auth/2fa/setup')
}

/**
 * 启用当前管理员 2FA — 校验 secret 对应的 6 位 TOTP 动态码。
 * @param config 透传 axios 配置（如 skipErrorToast，便于在弹框内自行展示错误）。
 */
export function enableTwoFactor(
  data: TwoFactorEnableRequest,
  config?: Parameters<typeof post>[2]
): Promise<TwoFactorEnableResponse> {
  return post<TwoFactorEnableResponse>('/admin/v1/auth/2fa/enable', data, config)
}

/**
 * 422 且业务码为此值：当前密码不正确（后端 #4108 `AdminInvalidCurrentPasswordException`）。
 * 调用方应把错误落到「当前密码」输入项下，而非顶部提示。
 */
export const ADMIN_INVALID_CURRENT_PASSWORD = 'ADMIN_INVALID_CURRENT_PASSWORD'

/**
 * 管理员自助修改登录密码。见后端 #4108。
 * 该端点登记在 2FA `sensitive_actions`：不带码提交命中 403 `ADMIN_2FA_REQUIRED` 时由调用方
 * 弹验证码框，再带 `twoFactorCode` 重试（`withTwoFactor` 注入 header 并关闭全局错误 toast）。
 * 成功后当前会话保持有效，其它会话被撤销。
 */
export function changePassword(
  data: ChangePasswordRequest,
  options?: TwoFactorOptions
): Promise<ChangePasswordResponse> {
  return post<ChangePasswordResponse>(
    '/admin/v1/auth/password/change',
    data,
    withTwoFactor(options)
  )
}
